import { ArrowTopRightOnSquareIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { DocumentCheckIcon, TrashIcon } from '@/components/icons'
import { Modal } from '@/components/ui/Modal'
import {
  createRepairOrderFile,
  deleteRepairOrderFile,
  returnRepairOrderToIntake,
  updateRepairOrder,
} from '@/services/repairOrders.service'
import { toast } from '@/utils/helpers'

import { isAdvisorRequiredForOl } from './advisor'
import DealerClosedTag from './components/DealerClosedTag'
import TransitionPreview from './components/TransitionPreview'
import {
  ACCEPTED_EXTENSIONS,
  CLOSED_TRANSITIONS,
  INTAKE_TRANSITIONS,
  RESOLVED_STATUSES,
} from './constants'

// Store each selected file with a client-side id so duplicate files can coexist
// and list/remove operations don't depend on array indexes.
const wrapFile = (raw) => ({ id: crypto.randomUUID(), raw })

const formatTimestamp = (value) => (value ? dayjs(value).format('MM/DD/YYYY h:mm A') : '—')

// Which side logged the RO, by the API's user type.
const AUTHOR_SIDE = { 'WPC User': 'WPC', 'Dealer User': 'Dealer' }

const addedBy = (ro) => {
  if (!ro.createdByName) return '—'
  const side = AUTHOR_SIDE[ro.createdByType]
  return side ? `${ro.createdByName} (${side})` : ro.createdByName
}

const SetStatusModal = ({
  open,
  setOpen,
  repairOrder,
  dealerId,
  dealer,
  isDealerUser,
  isDealerAdmin,
  onSuccess,
  initialStatus,
  initialDealerClosed,
  initialNewFiles,
}) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  // Dealer Closed is WPC's to set, and a self-managed dealer's own admins' (WARP-967): the
  // same rule as the RO tables' rows, and the API's. Anyone else sees the flag, not the
  // toggle.
  const canSetDealerClosed = !isDealerUser || (isDealerAdmin && Boolean(dealer?.isDealerManaged))

  // RO Number
  const [roNumber, setRoNumber] = useState('')
  const [roNumberDirty, setRoNumberDirty] = useState(false)

  // Files
  const [existingFiles, setExistingFiles] = useState([])
  const [filesToDelete, setFilesToDelete] = useState([])
  const [newFiles, setNewFiles] = useState([])
  const fileInputRef = useRef(null)

  // Status
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [dealerClosed, setDealerClosed] = useState(false)

  // OL fields
  const [advisor, setAdvisor] = useState('')
  const [advisorError, setAdvisorError] = useState('')
  const [additionalDollars, setAdditionalDollars] = useState('')
  const [isOpen, setIsOpen] = useState(true)
  const [isAgesOutOverridden, setIsAgesOutOverridden] = useState(false)
  const [timeClockTriggerDate, setTimeClockTriggerDate] = useState('')
  const [agesOut, setAgesOut] = useState('')
  const [isDateAddedOverridden, setIsDateAddedOverridden] = useState(false)
  const [openedAt, setOpenedAt] = useState('')

  // Return to intake (undo "put on OL")
  const [confirmReturnToIntake, setConfirmReturnToIntake] = useState(false)
  const [returningToIntake, setReturningToIntake] = useState(false)

  const today = dayjs().format('YYYY-MM-DD')

  const timeClockTrigger = dealer?.manufacturer?.timeClockTrigger
  const isClosedSubmitted = repairOrder?.status === 'Closed — Submitted'
  const isClosed = isClosedSubmitted || repairOrder?.status === 'Closed'
  const hasOlDetails = repairOrder?.state != null
  const isResolved = RESOLVED_STATUSES.includes(repairOrder?.status)
  // FSM-resolved ROs (state === 'Resolved') carry an OutstandingListDetails row and must be
  // unresolved through the flow to move back onto the list — a bare status write would leave
  // status and FSM state inconsistent. Plain intake "Closed" labels have no OL details/state.
  const isResolvedState = repairOrder?.state === 'Resolved'
  const isOnOutstandingListStatus = repairOrder?.status === 'On Outstanding List'
  const isOnOL = selectedStatus === 'On Outstanding List'
  const isAdvisorRequired = isAdvisorRequiredForOl({
    selectedStatus,
    currentStatus: repairOrder?.status,
    dealerClosed,
  })
  // "Adding to the OL" (fresh OL details created) happens whenever an RO with no OL details is
  // moved onto the list — from Not Started, or from a plain intake "Closed"/"Closed — Submitted".
  // A claim that has been on the list before is moving back onto it, not being added.
  const isNewToOL = isOnOL && !hasOlDetails
  // A paid/written-off claim can be reopened straight from Intake, but only when its OL
  // record is actually in the Resolved state — that's the only source the unresolve
  // transition accepts, so offering it otherwise would surface a server error.
  const canReopenOntoOl = isResolved && isResolvedState
  // Such a claim has already been paid out or written off, so the repair order is closed at
  // the dealership. It goes back onto the list as a closed RO rather than an active one.
  const isReopeningAsClosedRo = isOnOL && canReopenOntoOl

  // "Add" a claim new to the list, "Reopen" a resolved one, "Move" one that's been on the
  // list before — so the buttons and the validation messages describe the same action.
  let olVerb = 'Move'
  let olVerbLower = 'move'
  if (isNewToOL) {
    olVerb = 'Add'
    olVerbLower = 'add'
  } else if (canReopenOntoOl) {
    olVerb = 'Reopen'
    olVerbLower = 'reopen'
  }
  const olSubmitLabel = canReopenOntoOl ? 'Reopen onto OL' : `${olVerb} to OL`

  // Transitions offered from a Closed / Closed — Submitted RO (WARP-957, 2026-09-24):
  //   Closed               → Outstanding List, or Closed — Submitted. Never straight to Paid:
  //                          "It could only be closed and submitted then paid." (Mark)
  //   Closed — Submitted   → Outstanding List, or its paid outcome (no dollars in V1).
  // Write-offs only happen on the Outstanding List, with dealer approval, so none here.
  const closedTransitionOptions = [
    { value: 'On Outstanding List', label: 'On Outstanding List' },
    ...(isClosedSubmitted
      ? CLOSED_TRANSITIONS
      : [{ value: 'Closed — Submitted', label: 'Closed — Submitted' }]),
  ]
  // An RO that was closed in the DMS goes back on the list as a closed RO: write-off only,
  // never Internal. The API enforces it; the form shows it rather than offering a toggle.
  const goesOnClosed = isOnOL && isClosed
  const opensOnList = goesOnClosed ? false : isOpen

  // Sync state whenever the modal opens for a new RO. Keying on `open` (not just
  // repairOrder) ensures state repopulates even if the same row is reopened.
  useLayoutEffect(() => {
    if (!open || !repairOrder) return
    setRoNumber(repairOrder.roNumber || '')
    setRoNumberDirty(false)
    setExistingFiles(repairOrder.files || [])
    setFilesToDelete([])
    // The ready queue (WARP-951) opens this with the row's choices already made: the
    // Outstanding List disposition, plus any dealer-closed flag and files staged on the
    // row, so nothing picked there is lost on the way into the OL form.
    setNewFiles(initialNewFiles || [])
    setSelectedStatus(initialStatus || null)
    setDealerClosed(initialDealerClosed ?? (repairOrder.dealerClosed || false))
    setAdvisor('')
    setAdvisorError('')
    setAdditionalDollars('')
    setIsOpen(true)
    setIsAgesOutOverridden(false)
    setTimeClockTriggerDate('')
    setAgesOut('')
    setIsDateAddedOverridden(false)
    setOpenedAt('')
    setConfirmReturnToIntake(false)
  }, [open, repairOrder])

  const reset = () => {
    setRoNumber('')
    setRoNumberDirty(false)
    setExistingFiles([])
    setFilesToDelete([])
    setNewFiles([])
    setSelectedStatus(null)
    setDealerClosed(false)
    setAdvisor('')
    setAdvisorError('')
    setAdditionalDollars('')
    setIsOpen(true)
    setIsAgesOutOverridden(false)
    setTimeClockTriggerDate('')
    setAgesOut('')
    setIsDateAddedOverridden(false)
    setOpenedAt('')
    setConfirmReturnToIntake(false)
  }

  const handleReturnToIntake = async () => {
    setReturningToIntake(true)
    const response = await returnRepairOrderToIntake(
      dealerId,
      repairOrder.id,
      () => {},
      (err) => toast(err || 'Something went wrong.', 'error'),
      () => {},
    )
    setReturningToIntake(false)

    if (response) {
      toast('Repair order returned to intake.', 'success')
      reset()
      setOpen(false)
      onSuccess()
    }
  }

  const markFileForDeletion = (fileId) => {
    setFilesToDelete((prev) => [...prev, fileId])
    setExistingFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  const removeNewFile = (fileId) => {
    setNewFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  const handleUpdate = async (andNavigate = false) => {
    // The time clock trigger date is a real-world past event (e.g. the RO close date); it
    // can't be in the future.
    if (isOnOL && !isAgesOutOverridden && timeClockTriggerDate && timeClockTriggerDate > today) {
      toast('The time clock trigger date can’t be in the future.', 'error')
      return
    }

    // An RO can't go onto the Outstanding List without an Advisor # — see
    // isAdvisorRequiredForOl for the rule and why the dealer-closed exemption is scoped to
    // the move out of Intake.
    if (isAdvisorRequired && !advisor.trim()) {
      setAdvisorError(
        `An Advisor # is required to ${olVerbLower} this RO onto the Outstanding List.`,
      )
      toast(`Enter an Advisor # to ${olVerbLower} this RO onto the Outstanding List.`, 'error')
      return
    }

    setLoading(true)

    try {
      // 1. Delete marked files
      if (filesToDelete.length > 0) {
        await Promise.all(
          filesToDelete.map((fileId) => deleteRepairOrderFile(dealerId, repairOrder.id, fileId)),
        )
      }

      // 2. Upload new files
      if (newFiles.length > 0) {
        await Promise.all(
          newFiles.map(({ raw }) => {
            const formData = new FormData()
            formData.append('file', raw)
            formData.append('display_name', raw.name)
            return createRepairOrderFile(dealerId, repairOrder.id, formData)
          }),
        )
      }

      // 3. Build update payload
      const payload = {}

      if (roNumberDirty && roNumber.trim() && roNumber.trim() !== repairOrder.roNumber) {
        payload.roNumber = roNumber.trim()
      }

      if (canSetDealerClosed && dealerClosed !== (repairOrder.dealerClosed || false)) {
        payload.dealerClosed = dealerClosed
      }

      if (selectedStatus && !isDealerUser) {
        if (selectedStatus === 'On Outstanding List' && isResolvedState) {
          // Resolved RO returning to the active list — drive the FSM back to WPC Review via
          // the unresolve transition (which also resets status to On Outstanding List) instead
          // of writing status directly, so state and status stay consistent.
          payload.markAsUnresolved = true
        } else {
          payload.status = selectedStatus
        }

        // Sent on every move onto the list, including the unresolve path and when blank.
        // Omitting it would leave whatever advisor the RO's previous OL stint wrote, which
        // looks to the user like the system filling in an advisor they never entered.
        if (selectedStatus === 'On Outstanding List') {
          payload.advisor = advisor.trim()
        }

        // A reopened paid/written-off claim goes back on as a closed RO — the repair order
        // is closed in the dealer's DMS once it's been paid out or written off.
        if (isReopeningAsClosedRo) {
          payload.isOpen = false
        }

        // OL entry fields only apply when a fresh OL record is being created. A claim that
        // has been on the list before already has one, and its age-out basis and open state
        // belong to that record — sending this form's defaults would overwrite them with
        // values the user was never shown.
        if (selectedStatus === 'On Outstanding List' && !hasOlDetails) {
          if (opensOnList && additionalDollars) {
            payload.additionalDollarsHeldUp = additionalDollars
          }
          payload.isOpen = opensOnList
          payload.isAgesOutOverridden = isAgesOutOverridden

          if (isAgesOutOverridden && agesOut) {
            payload.agesOut = agesOut
            payload.timeClockTriggerDate = null
          } else if (timeClockTriggerDate) {
            payload.timeClockTriggerDate = timeClockTriggerDate
            payload.agesOut = null
          }

          payload.isDateAddedOverridden = isDateAddedOverridden
          if (isDateAddedOverridden && openedAt) {
            // "Date Added" = when the RO was added to the OL = OutstandingListDetails.opened_at.
            // created_at is read-only on the backend (immutable system insert time).
            payload.openedAt = openedAt
          }
        }
      }

      // getErrorMessage returns null when the response interceptor has already reported
      // the failure — an expired session is the case that does this. A second message
      // for one problem is noise, so record it and report nothing more.
      let errorAlreadyReported = false

      // Only call update if there's something to patch
      if (Object.keys(payload).length > 0) {
        const response = await updateRepairOrder(
          dealerId,
          repairOrder.id,
          payload,
          () => {},
          (err) => {
            if (!err) {
              errorAlreadyReported = true
              return
            }
            // Backstop for a server-side advisor rejection: put it under the Advisor #
            // input rather than leaving it as a toast alone. getErrorMessage flattens
            // DRF's {"advisor": [...]} to the message, so the key is gone by here —
            // match the requirement text instead. The check above should catch this
            // first; this is what keeps the field labeled if the two rules drift.
            if (typeof err === 'string' && /advisor\s*#/i.test(err)) setAdvisorError(err)
            throw new Error(err)
          },
          () => {},
        )

        if (!response) {
          if (errorAlreadyReported) return
          throw new Error('Update failed')
        }
      }

      toast('Repair order updated.', 'success')
      reset()
      setOpen(false)
      onSuccess()

      if (andNavigate && selectedStatus === 'On Outstanding List') {
        navigate(`/dealer/${dealerId}/repair-order/${repairOrder.id}`)
      }
    } catch (err) {
      toast(err?.message || 'Something went wrong.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const hasStatusChange = selectedStatus && selectedStatus !== repairOrder?.status
  const hasFileChanges = newFiles.length > 0 || filesToDelete.length > 0
  const hasRoNumberChange = roNumberDirty && roNumber.trim() !== (repairOrder?.roNumber || '')
  const hasDealerClosedChange = dealerClosed !== (repairOrder?.dealerClosed || false)
  const hasChanges =
    hasStatusChange || hasFileChanges || hasRoNumberChange || hasDealerClosedChange
  // Every move onto the list gets the save-and-go-to-OL path, not just a first-time add.
  // A claim that has been on the list before is still landing back on it, and leaving it with
  // only a bare "Update Status" button was the dead end reported on this ticket.
  const actions = isOnOL
    ? [
        { type: 'cancel', label: 'Cancel' },
        {
          type: 'submit',
          label: olSubmitLabel,
          onClick: () => handleUpdate(false),
          disabled: !hasChanges,
        },
        {
          type: 'submit_and_continue',
          label: `${olVerb} & Go to OL`,
          onClick: () => handleUpdate(true),
          disabled: !hasChanges,
        },
      ]
    : [
        { type: 'cancel', label: 'Cancel' },
        {
          type: 'submit',
          label: hasStatusChange ? 'Update Status' : 'Save Changes',
          onClick: () => handleUpdate(false),
          disabled: !hasChanges,
        },
      ]

  // --- File section ---
  const visibleFiles = existingFiles.filter((f) => !filesToDelete.includes(f.id))

  const filesSection = (
    <div>
      <h3 className="text-charcoal-400 mb-3 text-sm font-bold uppercase tracking-widest">
        Attachments
      </h3>

      {(visibleFiles.length > 0 || newFiles.length > 0) && (
        <div className="mb-2 space-y-1">
          {visibleFiles.map((file) => (
            <div key={file.id} className="flex items-center justify-between text-blue-800">
              <div className="flex items-center overflow-x-hidden">
                <DocumentCheckIcon className="mr-2 h-5 w-5 shrink-0" />
                <a
                  href={file.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-sm underline"
                >
                  {file.displayName || 'File'}
                </a>
              </div>
              <button
                type="button"
                aria-label="Delete"
                className="inline-flex shrink-0 items-center rounded-lg bg-transparent text-center text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => markFileForDeletion(file.id)}
              >
                <TrashIcon className="text-charcoal-800 h-5 w-5" />
              </button>
            </div>
          ))}
          {newFiles.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between text-blue-800">
              <div className="flex items-center overflow-x-hidden">
                <DocumentCheckIcon className="mr-2 h-5 w-5 shrink-0" />
                <span className="truncate text-sm underline">{entry.raw.name}</span>
                <span className="text-charcoal-400 ml-2 text-xs italic">(new)</span>
              </div>
              <button
                type="button"
                aria-label="Delete"
                className="inline-flex shrink-0 items-center rounded-lg bg-transparent text-center text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={() => removeNewFile(entry.id)}
              >
                <TrashIcon className="text-charcoal-800 h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS.join(',')}
        className="hidden"
        onChange={(e) => {
          if (e.target.files.length > 0) {
            setNewFiles((prev) => [...prev, ...Array.from(e.target.files).map(wrapFile)])
          }
          e.target.value = ''
        }}
      />
      <button
        type="button"
        className="border-charcoal-200 text-charcoal-700 flex h-[46px] w-full items-center gap-2 rounded-lg border border-dashed bg-white px-3 text-sm transition-colors hover:border-blue-800 hover:text-blue-800"
        onClick={() => fileInputRef.current?.click()}
      >
        <PaperClipIcon className="h-4 w-4 shrink-0" />
        <span>
          {visibleFiles.length > 0 || newFiles.length > 0
            ? 'Add more files...'
            : 'Choose files...'}
        </span>
      </button>
    </div>
  )

  // --- Status section ---
  // The Advisor # is entered on every move onto the list, so it's shared between the full
  // OL entry form (new to the list) and the standalone prompt below (returning to it).
  // autoComplete is pinned off (TextInput already defaults to it) and stated explicitly here
  // because it matters at this field: a browser-remembered advisor from another RO would be
  // written straight onto this repair order.
  // Derived rather than stored, so toggling "Dealer Closed" on (which drops the requirement)
  // can't leave a stale error hanging under a field that's no longer required.
  const visibleAdvisorError = isAdvisorRequired ? advisorError : ''

  const advisorField = (
    <div>
      <label
        htmlFor="ol-advisor"
        className="text-charcoal-400 mb-1 block text-[10px] font-bold uppercase tracking-widest"
      >
        ADV #{' '}
        {isAdvisorRequired && (
          <span className="text-error" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <TextInput
        id="ol-advisor"
        name="ol-advisor"
        autoComplete="off"
        value={advisor}
        onChange={(e) => {
          setAdvisor(e.target.value)
          if (advisorError) setAdvisorError('')
        }}
        error={visibleAdvisorError}
        placeholder=""
        aria-required={isAdvisorRequired}
        aria-invalid={Boolean(visibleAdvisorError)}
        aria-describedby={visibleAdvisorError ? 'error:ol-advisor' : undefined}
      />
    </div>
  )

  // Shown instead of the full grid when a claim that has been on the list before is moved back
  // onto it: its OL record already holds the age-out basis and open state, so the Advisor # is
  // the only thing to collect (it is deliberately not carried over from the previous stint).
  const returningToOlFields = (
    <div className="mt-6">
      <p className="text-charcoal-700 mb-3 text-sm">
        This repair order was on the Outstanding List before. Its age out date and other list
        details are kept — confirm the Advisor # handling it now.
      </p>
      <div className="w-1/2 pr-4">{advisorField}</div>
    </div>
  )

  // Shared OL-detail entry form, shown when an RO with no OL record is being placed on the list
  // (from Not Started or from a plain intake Closed / Closed — Submitted).
  const olFieldsGrid = (
    <>
      {dealerClosed && (
        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm font-medium text-blue-900">
            This claim is marked <span className="font-bold">Dealer Closed</span>. It will be added
            to the Outstanding List as a closed repair order.
          </p>
        </div>
      )}
      <div className="mt-6 grid grid-cols-2 gap-x-8">
        <div className="flex flex-col gap-4">
          <h3 className="text-charcoal-900 text-sm font-bold leading-5">General</h3>

          {advisorField}

          {opensOnList && (
            <div>
              <label
                htmlFor="ol-additional-dollars"
                className="text-charcoal-400 mb-1 block text-[10px] font-bold uppercase tracking-widest"
              >
                Additional Dollars Held Up
              </label>
              <TextInput
                id="ol-additional-dollars"
                value={additionalDollars}
                onChange={(e) => setAdditionalDollars(e.target.value)}
                placeholder="$0.00"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <h3 className="text-charcoal-900 text-sm font-bold leading-5">Age Out Date</h3>

          {goesOnClosed ? (
            <p className="text-charcoal-600 text-sm">
              Goes on as a <span className="font-semibold">closed</span> RO: it was closed in the
              DMS, so it can be written off but not marked Internal.
            </p>
          ) : (
            <Toggle label="Open RO?" checked={isOpen} onChange={setIsOpen} />
          )}
          <Toggle
            label="Requires Age Out Override"
            checked={isAgesOutOverridden}
            onChange={setIsAgesOutOverridden}
          />
          <Toggle
            label="Requires Date Added Override"
            checked={isDateAddedOverridden}
            onChange={setIsDateAddedOverridden}
          />

          {opensOnList && !isAgesOutOverridden && timeClockTrigger === 'RO close date' ? (
            <p className="text-center text-xs font-light italic leading-5 tracking-[1.25px]">
              For this manufacturer, all open Repair Orders will age out 30 days after the date
              added to the Outstanding List.
            </p>
          ) : (
            <div>
              <label className="text-charcoal-400 mb-1 block text-[10px] font-bold uppercase tracking-widest">
                {isAgesOutOverridden
                  ? 'Age Out Date Override (MM/DD/YYYY)'
                  : `${timeClockTrigger || 'Last Tech Punch Date'} (MM/DD/YYYY)`}
              </label>
              <TextInput
                type="date"
                value={isAgesOutOverridden ? agesOut : timeClockTriggerDate}
                // Age-out override is a future date by nature; the time clock trigger is a
                // past real-world event, so cap it at today.
                max={isAgesOutOverridden ? undefined : today}
                onChange={(e) =>
                  isAgesOutOverridden
                    ? setAgesOut(e.target.value)
                    : setTimeClockTriggerDate(e.target.value)
                }
              />
            </div>
          )}

          {isDateAddedOverridden && (
            <div>
              <label
                htmlFor="ol-created-at"
                className="text-charcoal-400 mb-1 block text-[10px] font-bold uppercase tracking-widest"
              >
                Date Added Override (MM/DD/YYYY)
              </label>
              <TextInput
                id="ol-created-at"
                type="date"
                value={openedAt}
                onChange={(e) => setOpenedAt(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )

  let statusSection

  if (isDealerUser) {
    statusSection = null
  } else if (isClosed) {
    // Closed / Closed — Submitted can move back onto the Outstanding List (bug: previously a
    // dead-end), be corrected between the two closed states, or have their paid outcome logged.
    statusSection = (
      <div>
        <h3 className="text-charcoal-400 mb-3 text-sm font-bold uppercase tracking-widest">
          Set Status
        </h3>
        <div className="flex flex-col gap-3">
          {closedTransitionOptions.map((opt) => (
            <label key={opt.value} className="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="ro-status"
                className="h-5 w-5 text-blue-800 focus:ring-blue-800"
                checked={selectedStatus === opt.value}
                onChange={() => setSelectedStatus(opt.value)}
              />
              <span className="text-charcoal-900 text-base font-medium">{opt.label}</span>
            </label>
          ))}
        </div>

        {selectedStatus && <TransitionPreview targetStatus={selectedStatus} />}

        {/* Placing a closed intake RO (no OL record) onto the list needs the full OL entry
            form. One that has been on the list before keeps its existing OL record and only
            needs the Advisor # re-entered. */}
        {isOnOL && (hasOlDetails ? returningToOlFields : olFieldsGrid)}
      </div>
    )
  } else if (!hasOlDetails) {
    // No OL record — can transition freely between intake statuses
    statusSection = (
      <div>
        <h3 className="text-charcoal-400 mb-3 text-sm font-bold uppercase tracking-widest">
          Set Status
        </h3>
        <div className="flex flex-col gap-3">
          {INTAKE_TRANSITIONS.map((opt) => (
            <label key={opt.value} className="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="ro-status"
                className="h-5 w-5 text-blue-800 focus:ring-blue-800"
                checked={selectedStatus === opt.value}
                onChange={() => setSelectedStatus(opt.value)}
              />
              <span className="text-charcoal-900 text-base font-medium">{opt.label}</span>
            </label>
          ))}
        </div>

        {selectedStatus && <TransitionPreview targetStatus={selectedStatus} />}

        {/* OL-specific fields */}
        {isOnOL && olFieldsGrid}
      </div>
    )
  } else if (!isResolved) {
    // On OL and still active — status changes are managed on the OL detail page
    statusSection = (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm font-medium text-blue-900">
          This RO is on the Outstanding List. Status changes are managed from the OL detail page.
        </p>
        <Link
          to={`/dealer/${dealerId}/repair-order/${repairOrder?.id}`}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-800 underline"
          onClick={(e) => {
            e.stopPropagation()
            reset()
            setOpen(false)
          }}
        >
          Go to OL Detail Page
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    )
  } else if (canReopenOntoOl) {
    // Resolved claim being reopened from Intake. The RO is closed in the dealer's DMS by
    // the time it's been paid or written off, so it goes back onto the list as a closed
    // RO — the worker reopens line items from the OL detail page afterwards.
    statusSection = (
      <div>
        <h3 className="text-charcoal-400 mb-3 text-sm font-bold uppercase tracking-widest">
          Set Status
        </h3>
        <p className="text-charcoal-700 mb-3 text-sm">
          This RO is <span className="font-bold">{repairOrder?.status}</span>. Reopening it puts it
          back on the Outstanding List.
        </p>
        <label className="flex cursor-pointer items-center gap-3">
          <input
            type="radio"
            name="ro-status"
            className="h-5 w-5 text-blue-800 focus:ring-blue-800"
            checked={isOnOL}
            onChange={() => setSelectedStatus('On Outstanding List')}
          />
          <span className="text-charcoal-900 text-base font-medium">
            Reopen onto the Outstanding List
          </span>
        </label>

        {isOnOL && (
          <>
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm font-medium text-blue-900">
                This claim has already been paid out or written off, so the repair order is closed
                at the dealership. It will go back onto the Outstanding List as a{' '}
                <span className="font-bold">closed</span> repair order.
              </p>
            </div>
            {returningToOlFields}
          </>
        )}
      </div>
    )
  } else {
    // Resolved, but the OL record isn't in a state the reopen transition accepts —
    // fall back to the detail page rather than offering an action that would fail.
    statusSection = (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-charcoal-700 text-sm font-medium">
          This RO is <span className="font-bold">{repairOrder?.status}</span>. To make changes,
          unresolve it from the OL detail page.
        </p>
        <Link
          to={`/dealer/${dealerId}/repair-order/${repairOrder?.id}`}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-800 underline"
          onClick={(e) => {
            e.stopPropagation()
            reset()
            setOpen(false)
          }}
        >
          Go to OL Detail Page
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    )
  }

  return (
    <Modal
      open={open}
      setOpen={(val) => {
        if (!val) reset()
        setOpen(val)
      }}
      title="Manage Repair Order"
      width="large"
      loading={loading}
      content={
        <div className="mt-4 space-y-6">
          {/* RO Number */}
          <div>
            <label
              htmlFor="set-status-ro-number"
              className="text-charcoal-400 mb-1 block text-[10px] font-bold uppercase tracking-widest"
            >
              Repair Order Number
            </label>
            <TextInput
              id="set-status-ro-number"
              value={roNumber}
              onChange={(e) => {
                setRoNumber(e.target.value)
                setRoNumberDirty(true)
              }}
              placeholder="Enter RO number"
              disabled={hasOlDetails}
              infoTooltip={
                hasOlDetails
                  ? 'The repair order number is locked once the RO is on the Outstanding List. Return it to intake to change it.'
                  : null
              }
            />
          </div>

          {/* What only the RO Claims view used to show (2026-09-28): when the RO came in,
              when it last changed, and who logged it. WPC counts who logs ROs, the dealer
              login or WPC, so the author's side shows beside the name. */}
          {repairOrder && (
            <dl className="border-charcoal-100 grid grid-cols-3 gap-4 border-y py-3">
              {[
                ['Date Received', formatTimestamp(repairOrder.receivedAt)],
                ['Last Updated', formatTimestamp(repairOrder.modifiedAt)],
                ['Added By', addedBy(repairOrder)],
              ].map(([term, value]) => (
                <div key={term}>
                  <dt className="text-charcoal-400 mb-1 text-[10px] font-bold uppercase tracking-widest">
                    {term}
                  </dt>
                  <dd className="text-charcoal-900 text-sm">{value}</dd>
                </div>
              ))}
            </dl>
          )}

          {filesSection}
          {statusSection}

          {/* Undo "put on OL" — for intake dealers' ROs actively on the OL. Only shown while the
              status is On Outstanding List: once an RO is Closed/Submitted/Paid/Resolved, "return
              to intake" no longer applies. Limited to WPC users and dealer admins (not dealer
              service users), matching the OL edit modal; the backend gates on RO Intake enabled. */}
          {(!isDealerUser || isDealerAdmin) &&
            hasOlDetails &&
            isOnOutstandingListStatus &&
            dealer?.isRoIntakeEnabled && (
              <div className="border-charcoal-200 rounded-lg border border-dashed p-4">
                {confirmReturnToIntake ? (
                  <div className="space-y-3">
                    <p className="text-charcoal-700 text-sm">
                      Remove this RO from the Outstanding List and return it to a clean intake
                      state? Its line items and comments will be cleared (kept in the audit log).
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleReturnToIntake}
                        disabled={returningToIntake}
                        className="text-error border-error hover:bg-error/5 inline-flex items-center rounded-lg border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Return to Intake
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmReturnToIntake(false)}
                        disabled={returningToIntake}
                        className="text-charcoal-700 hover:bg-charcoal-50 inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmReturnToIntake(true)}
                    className="text-charcoal-700 hover:text-charcoal-900 text-sm font-medium underline"
                  >
                    Return to Intake (remove from Outstanding List)
                  </button>
                )}
              </div>
            )}

          {canSetDealerClosed ? (
            <Toggle label="Dealer Closed" checked={dealerClosed} onChange={setDealerClosed} />
          ) : (
            dealerClosed && (
              <p className="text-charcoal-700 flex items-center gap-2 text-sm">
                <DealerClosedTag />
                Dealer Closed: closed in the dealer&apos;s DMS
              </p>
            )
          )}
        </div>
      }
      actions={actions}
      style={{ minHeight: '50vh' }}
    />
  )
}

SetStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  repairOrder: PropTypes.object,
  dealerId: PropTypes.string.isRequired,
  dealer: PropTypes.object,
  isDealerUser: PropTypes.bool,
  isDealerAdmin: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired,
  initialStatus: PropTypes.string,
  initialDealerClosed: PropTypes.bool,
  // Staged files in this modal's own `{ id, raw }` shape.
  initialNewFiles: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, raw: PropTypes.any }),
  ),
}

export default SetStatusModal
